import { logger } from '@adornis/base/logging.js';
import type { ValueEvent } from '@adornis/base/utilTypes.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import type { ColumnDefinition } from '@adornis/data-view/ColumnDefinition.js';
import type { DataViewOptions } from '@adornis/data-view/client/data-view-options.js';
import '@adornis/data-view/client/x-data-view.js';
import { View } from '@adornis/data-view/db/view.js';
import { XNativeDialog } from '@adornis/dialog/x-native-dialog.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { subscribeSearchLocation } from '@ils/locations/api/location.js';
import { Location } from '@ils/locations/db/location.js';
import { permissionGuard } from '@ils/permissions/client/permissionGuard.js';
import { html } from 'lit';
import { customElement } from 'lit/decorators.js';
import { state } from 'lit/decorators/state.js';
import { map } from 'rxjs';
import { ILSLocationEdit } from './ils-location-edit.js';

@customElement('ils-locations')
export class ILSLocations extends ChemistryLitElement {
  override styles() {
    return [{ ':host': { flex: '1', display: 'flex', flexDirection: 'column' } }];
  }

  private readonly _filter = new AdornisFilter({
    search: '',
    searchFields: [
      'displayName',
      'address.name',
      'address.streetAndHousenumber',
      'address.zipCity.zip',
      'address.zipCity.city',
      'address.bundesland',
      'address.additionalInfo',
    ],
    sortingFields: [],
    sortingDirections: [],
  });

  @state() private readonly _view = new RXController(this, this._filter.whenChanging, filter$ =>
    filter$.pipe(map(filter => new View({ filter }))),
  );

  override render() {
    return html`
      <x-flex flex ${css({ minHeight: '0' })}>
        <x-flex ${css({ borderBottom: `1px solid ${this.colors.neutral.shade(300)}` })}>
          <x-flex horizontal wrap space="md" padding="xs">
            <x-input
              flex
              clearable
              placeholder="Suchen..."
              placeholder-mode="hide"
              @value-changed=${(e: ValueEvent<string>) => {
                if (this._filter.search !== e.detail.value) this._filter.search = e.detail.value;
              }}
            ></x-input>
            ${permissionGuard({
              resource: 'locations',
              action: 'create',
              guardedTemplate: html`
                <x-button icon="add" @click=${() => ILSLocationEdit.showPopup()}>Standort hinzufügen</x-button>
              `,
            })}
          </x-flex>
        </x-flex>
        <x-flex flex> ${this.dataview()} </x-flex>
      </x-flex>
    `;
  }

  private dataview() {
    if (!this._view.value) return html` <x-loading-state></x-loading-state> `;
    return html`
      <x-data-view
        .options=${{
          class: Location,
          operation: subscribeSearchLocation,
          isSortable: true,
          isColumnResizable: false,
          isColumnResortable: false,
          loader: () =>
            html` <x-flex ${css({ minHeight: '200px' })} center crossaxis-center> <ils-loader></ils-loader> </x-flex>`,
          emptyState: () =>
            html`
              <x-flex
                ${css({
                  height: '200px',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gridColumn: '1 / -1',
                })}
              >
                <x-h3 center> 🦗 Keine Standorte gefunden </x-h3>
              </x-flex>
            `,
          view: this._view.value,
          customColumns: this._dataviewColumns(),
        } satisfies DataViewOptions<typeof Location>}
      ></x-data-view>
    `;
  }

  private _dataviewColumns(): Array<ColumnDefinition<typeof Location>> {
    return [
      {
        columnName: 'Name',
        fieldName: 'displayName',
        renderItem: location => html` <x-text bold> ${location.displayName} </x-text> `,
      },
      {
        columnName: 'Adresse',
        fieldName: 'address',
        renderItem: location => html` <x-text> ${location.address?.toString()} </x-text> `,
      },
      {
        columnName: 'Aktionen',
        customID: '__actions',
        renderItem: location => html`
          <x-flex horizontal>
            ${permissionGuard({
              resource: 'locations',
              action: 'modify',
              guardedTemplate: html`
                <x-button
                  mode="icon"
                  icon="edit"
                  @click=${(e: MouseEvent) => {
                    e.stopPropagation();
                    ILSLocationEdit.showPopup({ props: { locationID: location._id } });
                  }}
                >
                  <x-tooltip> <x-text> Standort bearbeiten </x-text> </x-tooltip>
                </x-button>
              `,
            })}
            ${permissionGuard({
              resource: 'locations',
              action: 'delete',
              guardedTemplate: html`
                <x-button
                  mode="icon"
                  icon="delete"
                  tone="error"
                  @click=${async (e: MouseEvent) => {
                    e.stopPropagation();
                    if (await XNativeDialog.confirm('Sind Sie sicher, dass Sie diesen Standort löschen wollen?')) {
                      try {
                        await location.remove(); // TODO do we want to be able to remove this? At least check assignment in beforeRemove
                        void XSnackbar.show('Standort gelöscht');
                      } catch (err) {
                        logger.error({ err }, 'Es gab ein Problem beim Löschen des Standorts');
                        void XSnackbar.show('Fehler beim Löschen des Standorts');
                      }
                    }
                  }}
                >
                  <x-tooltip> <x-text> Standort löschen </x-text> </x-tooltip>
                </x-button>
              `,
            })}
          </x-flex>
        `,
      },
    ];
  }
}
